import Head from 'next/head';
import {useRouter} from 'next/router';

import {BrowseCourseBadgesContext} from '@udemy/browse-course';
import {HeadSEO, useCanonicalUrl} from '@udemy/head-seo';
import {useI18n} from '@udemy/i18n';
import {META_DEFAULT_BASE_URL} from '@udemy/nextjs-core';
import {getProfessionalSkillsShowcaseDataByLocale} from '@udemy/react-discovery-units';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {SitelinkSearchBoxScript} from '@udemy/schema-markup';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {getLayout} from 'src/components/layout';
import {
    getBrowseCourseBadgesContextOptionsByExperiment,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
} from 'src/lib/experimentation-helper';

import {ProfessionalSkillsShowcaseDataProvider} from './contexts/professional-skills-showcase-data-context';
import {
    LoggedOutHomePageBody,
    LoggedOutHomePageBodyProps,
} from './logged-out-homepage-body.react-component';

const MX_SEARCH_URL = 'courses/search';

export function LoggedOutHomePageFeature(props: LoggedOutHomePageBodyProps) {
    const {Config: udConfig, request} = useUDData();
    const router = useRouter();
    const {gettext} = useI18n();
    const pageTitle = gettext('Udemy: Online Courses for Skills, Careers & AI');
    const pageDescription = gettext(
        'Learn in-demand skills with online courses, get professional certificates that advance your career, and explore courses in AI, coding, business and more.',
    );
    const {url: canonicalUrl} = useCanonicalUrl(
        // explicitly set asPath to '/' to avoid the issue where the canonical URL
        // picks up the nextjs '/lohp' path which is only used for local development
        {...router, asPath: '/'} as {locale: string; asPath: string},
        [],
        META_DEFAULT_BASE_URL,
    );

    // TODO: Clean up after subs catalog diff experiment is over https://udemyjira.atlassian.net/browse/CVR-151
    const {featureVariantAssignments} = useFeatures([SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE]);
    const subscriptionCatalogDifferentiatorFeature =
        featureVariantAssignments.get(SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE)?.configuration ??
        {};

    const psuData = getProfessionalSkillsShowcaseDataByLocale(request.language);

    return (
        <>
            <SitelinkSearchBoxScript
                brandUrl={udConfig.url.to_root}
                searchHandlerUri={`${udConfig.url.to_root}${MX_SEARCH_URL}`}
                headWrapper={Head}
            />
            <HeadSEO
                title={pageTitle}
                description={pageDescription}
                ogTitle={pageTitle}
                ogDescription={pageDescription}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            <SubscriptionPlansProvider
                subscriptionPlanQueryVariables={[
                    {
                        filters: {productType: SubscriptionPlanProductType.Consumersubscription},
                        marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                    },
                    {
                        filters: {
                            productType: SubscriptionPlanProductType.ConsumersubscriptionValuePlan,
                        },
                    },
                    {
                        filters: {productType: SubscriptionPlanProductType.Team},
                    },
                    {
                        filters: {productType: SubscriptionPlanProductType.GrowWithGoogle},
                    },
                    {
                        filters: {
                            productType:
                                SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle,
                        },
                    },
                ]}
            >
                <BrowseCourseBadgesContext.Provider
                    value={{
                        browseBadgeHandlerOptions: getBrowseCourseBadgesContextOptionsByExperiment(
                            subscriptionCatalogDifferentiatorFeature,
                        ),
                    }}
                >
                    <ProfessionalSkillsShowcaseDataProvider context={psuData}>
                        <LoggedOutHomePageBody {...props} />
                    </ProfessionalSkillsShowcaseDataProvider>
                </BrowseCourseBadgesContext.Provider>
            </SubscriptionPlansProvider>
        </>
    );
}

LoggedOutHomePageFeature.getLayout = getLayout;
